import type { Plugin } from "./plugin";

export interface DisclosureOptions {
  triggerSelector?: string;
  contentSelector?: string;
  expandedClass?: string;
  eventNameRaw?: string;
}

export abstract class Disclosure implements Plugin {
  protected trigger: HTMLElement | null = null;
  protected content: HTMLElement | null = null;
  protected options: DisclosureOptions;
  protected boundHandleToggle: (event: Event) => void;

  constructor(
    protected readonly element: HTMLElement,
    options: DisclosureOptions = {},
  ) {
    this.options = {
      triggerSelector: "[aria-expanded]",
      contentSelector: "[aria-hidden]",
      ...options,
    };

    if (this.options.triggerSelector) {
      this.trigger = element.querySelector(this.options.triggerSelector);
    }
    if (this.options.contentSelector) {
      this.content = element.querySelector(this.options.contentSelector);
    }
    this.boundHandleToggle = this.handleToggle.bind(this);
  }

  abstract init(): void;

  protected initDisclosure(): void {
    if (!this.trigger) {
      console.warn("Disclosure: Trigger not found", this.element);
      return;
    }

    this.bindDisclosureEvents();
  }

  protected bindDisclosureEvents(): void {
    this.trigger?.addEventListener("click", this.boundHandleToggle);
  }

  protected handleToggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  public toggle(): void {
    if (this.isExpanded()) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  public expand(): void {
    if (this.content) {
      this.content.setAttribute("aria-hidden", "false");
    }

    this.element.setAttribute("aria-expanded", "true");
    this.trigger?.setAttribute("aria-expanded", "true");

    this.dispatchDisclosureEvent("expanded");
  }

  public collapse(): void {
    if (this.content) {
      if (this.content.contains(document.activeElement)) {
        this.trigger?.focus();
      }
      this.content.setAttribute("aria-hidden", "true");
    }

    this.element.setAttribute("aria-expanded", "false");
    this.trigger?.setAttribute("aria-expanded", "false");

    this.dispatchDisclosureEvent("collapsed");
  }

  public isExpanded(): boolean {
    return (
      this.element.getAttribute("aria-expanded") === "true" ||
      this.trigger?.getAttribute("aria-expanded") === "true"
    );
  }

  protected dispatchDisclosureEvent(state: "expanded" | "collapsed"): void {
    const name = this.options.eventNameRaw
      ? `${this.options.eventNameRaw}:${state}`
      : `disclosure:${state}`;

    this.element.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: {
          expanded: state === "expanded",
        },
      }),
    );
  }

  public destroy(): void {
    this.trigger?.removeEventListener("click", this.boundHandleToggle);
  }
}
